import companyInfo from './companyInfo';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => {
  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(amount);
};

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

export const buildShopSettlementPrintHtml = (settlement) => {
  const shop = settlement?.shop || {};
  const orders = Array.isArray(settlement?.orders) ? settlement.orders : [];
  const commissionRate = Number.parseFloat(settlement?.commissionPercentage || 0) || 0;

  const resolvedCompany = {
    name: companyInfo.name,
    addressLines: (companyInfo.addressLines || []).filter(Boolean),
    phone: companyInfo.phone,
    alterphone: companyInfo.alterphone,
    email: companyInfo.email,
    gst: companyInfo.gst,
  };

  const totalOrderAmount = Number.parseFloat(settlement?.totalOrderAmount || 0) || 0;
  const totalCommissionAmount = Number.parseFloat(settlement?.commissionAmount || 0) || 0;
  const balancePayable = totalOrderAmount - totalCommissionAmount;
  const amountPaid = balancePayable;
  const totalPayable = amountPaid;

  const orderRows = orders
    .map((entry, index) => {
      const order = entry?.order || {};
      const rawAmount = Number.parseFloat(order.finalAmount || 0) || 0;
      const commissionAmount = Number(((rawAmount * commissionRate) / 100).toFixed(2));
      const payableAmount = Number((rawAmount - commissionAmount).toFixed(2));
      const identifier = order.showId || order.id || entry.orderId || '-';
      const deliveredAt = order.deliveredAt ? new Date(order.deliveredAt).toLocaleDateString() : '—';

      return `
        <tr>
          <td style="padding: 8px; border: 1px solid #D1D5DB; text-align: center;">${index + 1}</td>
          <td style="padding: 8px; border: 1px solid #D1D5DB;">${identifier}</td>
          <td style="padding: 8px; border: 1px solid #D1D5DB;">${deliveredAt}</td>
          <td style="padding: 8px; border: 1px solid #D1D5DB; text-align: right;">${formatCurrency(rawAmount)}</td>
          <td style="padding: 8px; border: 1px solid #D1D5DB; text-align: right;">${formatCurrency(commissionAmount)}</td>
          <td style="padding: 8px; border: 1px solid #D1D5DB; text-align: right;">${formatCurrency(payableAmount)}</td>
        </tr>
      `;
    })
    .join('');

  const companyDetailsHtml = [
    resolvedCompany.name,
    ...resolvedCompany.addressLines,
    resolvedCompany.phone ? `Phone: ${resolvedCompany.phone}` : '',
    resolvedCompany.alterphone ? `Alternative Phone: ${resolvedCompany.alterphone}` : '',
    resolvedCompany.email ? `Email: ${resolvedCompany.email}` : '',
    resolvedCompany.gst ? `GST: ${resolvedCompany.gst}` : '',
  ]
    .filter(Boolean)
    .map((line) => `<div>${line}</div>`)
    .join('');

  const shopAddressHtml = [
    shop.name,
    shop.addressLine1,
    shop.addressLine2,
    shop.city,
    shop.state,
  ]
    .filter(Boolean)
    .map((line) => `<div>${line}</div>`)
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charSet="utf-8" />
        <title>Shop Settlement</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 24px; color: #111827; }
          h1, h2, h3 { margin: 0; }
          .header { display: flex; justify-content: space-between; margin-bottom: 24px; }
          .section { margin-bottom: 24px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #F3F4F6; text-align: left; padding: 8px; border: 1px solid #D1D5DB; }
          .totals { margin-top: 16px; display: flex; justify-content: flex-end; }
          .totals table { width: auto; }
          .signature { margin-top: 48px; display: flex; justify-content: space-between; }
          .signature div { border-top: 1px solid #4B5563; padding-top: 8px; width: 200px; text-align: center; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>${resolvedCompany.name}</h1>
            <div>${companyDetailsHtml}</div>
          </div>
          <div style="text-align: right;">
            <h2>Shop Settlement</h2>
            <div>Settlement Date: ${formatDate(settlement?.settlementDate)}</div>
            <div>Settlement ID: ${settlement?.id}</div>
          </div>
        </div>

        <div class="section">
          <h3>Shop Details</h3>
          <div>${shopAddressHtml || shop.name || '—'}</div>
          <div>Commission: ${commissionRate.toFixed(2)}%</div>
        </div>

        <div class="section" style="padding-top:5px;">
          <table>
            <thead>
              <tr>
                <th style="width: 60px; text-align: center;">#</th>
                <th>Order ID</th>
                <th>Delivered On</th>
                <th style="text-align: right;">Order Amount</th>
                <th style="text-align: right;">Commission</th>
                <th style="text-align: right;">Payable</th>
              </tr>
            </thead>
            <tbody>
              ${orderRows || '<tr><td colspan="6" style="padding: 12px; text-align: center; border: 1px solid #D1D5DB;">No orders in this settlement</td></tr>'}
            </tbody>
          </table>

          <div class="totals">
            <table>
              <tbody>
                <tr>
                  <th style="padding: 8px; border: 1px solid #D1D5DB; text-align: left;">Total Order Amount</th>
                  <td style="padding: 8px; border: 1px solid #D1D5DB; text-align: right;">${formatCurrency(totalOrderAmount)}</td>
                </tr>
                <tr>
                  <th style="padding: 8px; border: 1px solid #D1D5DB; text-align: left;">Total Commission</th>
                  <td style="padding: 8px; border: 1px solid #D1D5DB; text-align: right;">${formatCurrency(totalCommissionAmount)}</td>
                </tr>
                <tr>
                  <th style="padding: 8px; border: 1px solid #D1D5DB; text-align: left;">Total Payable</th>
                  <td style="padding: 8px; border: 1px solid #D1D5DB; text-align: right;">${formatCurrency(totalPayable)}</td>
                </tr>
                <tr>
                  <th style="padding: 8px; border: 1px solid #D1D5DB; text-align: left;">Amount Paid</th>
                  <td style="padding: 8px; border: 1px solid #D1D5DB; text-align: right;">${formatCurrency(amountPaid)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="signature" style="padding-top:8px;">
          <div>For ${resolvedCompany.name}</div>
          <div>Shop Owner Signature</div>
        </div>
      </body>
    </html>
  `;
};

export const openShopSettlementPrintWindow = (settlement) => {
  if (!settlement) {
    throw new Error('Settlement not found');
  }
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Please allow pop-ups to print the settlement');
  }
  const html = buildShopSettlementPrintHtml(settlement);
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

export default buildShopSettlementPrintHtml;
